import React, { useState, useEffect } from "react";
import {
    Table,
    Button,
    Space,
    Input,
    Tooltip,
    Tag,
    Pagination,
    Row,
    Col, Popconfirm
} from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    PlusOutlined,
    FilterOutlined,
    CheckOutlined,
} from "@ant-design/icons";
import { CSVLink } from "react-csv";
// import { RiFileExcel2Line } from "react-icons/ri";
import './commonGrid.css';

const defaultRowsPerPage = 50;
const Grid = ({
    columnData,
    tableData, createButton,
    csvFileName,
    showEdit = true,
    showDelete = true,
    showView = true,
    handleEdit,
    handleDelete,
    handleView,
    rowKey = "id",
    paginationConfig,
    showPagination = true,
    className,
    handleCreate,
    filterButton,
    createButtonName,
    handleViewJson,
    enableRowSelection,
    expandableRowRender,
    onRowSelectionChange,
    searchButton = true,
    downloadCSV = true,
}) => {
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [sortedData, setSortedData] = useState([]);
    const [tableDataInternal, setTableDataInternal] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRowsPerPage);


    useEffect(() => {
        setTableDataInternal(tableData || []);
    }, [tableData]);

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleTableChange = (pagination, filters, sorter) => {
        let newData = tableDataInternal && Array.isArray(tableDataInternal) ? [...tableDataInternal] : [];

        // Apply search filter first
        if (searchText) {
            newData = newData.filter((record) =>
                Object.values(record).some(
                    (value) =>
                        value &&
                        typeof value === "string" &&
                        value.toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }

        // Apply filters
        for (const columnKey in filters) {
            const columnFilters = filters[columnKey];
            if (columnFilters && columnFilters.length > 0) {
                newData = newData.filter((record) =>
                    columnFilters.includes(String(record[columnKey]))
                );
            }
        }
        setFilteredData(newData);

        // Apply sorting
        if (sorter && sorter.columnKey && sorter.order) {
            newData.sort((a, b) => {
                const sortOrder = sorter.order === "ascend" ? 1 : -1;
                return (String(a[sorter.columnKey]) || "").localeCompare(String(b[sorter.columnKey]) || "") * sortOrder;
            });
        }
        setSortedData(newData);
    };

    const generateColumns = (data) => {
        if (!Array.isArray(data)) {
            console.error("Data is not an array");
            return [];
        }

        return data.map((column) => {
            const {
                title,
                dataIndex,
                key,
                feature,
                render,
                width,
                hidden,
                filters,
                fixed,
                tags,
            } = column;

            let columnConfig = {
                title,
                dataIndex,
                key,
                width,
                hidden,
                fixed,
                filters: dataIndex === "action" ? [] : filters,
            };

            // Sorter
            if (feature && feature.includes("sorter")) {
                columnConfig.sorter = (a, b) => {
                    const aValue = a[dataIndex] || "";
                    const bValue = b[dataIndex] || "";
                    return String(aValue).localeCompare(String(bValue));
                };
            }

            // Tags (e.g., status display)
            if (tags) {
                columnConfig.render = (text, record) => {
                    let tagText = text;
                    let tagColor = "blue";

                    switch (record[dataIndex]) {
                        case 1:
                            tagText = "Active";
                            tagColor = "green";
                            break;
                        case 2:
                            tagText = "Inactive";
                            tagColor = "red";
                            break;
                        case 3:
                            tagText = "Pending";
                            tagColor = "gold";
                            break;
                        default:
                            break;
                    }

                    return (
                        <Space>
                            <Tag color={tagColor}>{tagText}</Tag>
                        </Space>
                    );
                };
            }

            // Show tag style if set 'true' in any column
            if (column.tag === true) {
                columnConfig.render = (text) => {
                    // For boolean values like true/false
                    //'column.boolean': consider the 1 or 0 is true or false to set 'tag' if it is 'true'
                    if (column.boolean) {
                        const isTrue = text === true || text === 1;

                        return isTrue ? (
                            <Tag
                                color={column.tagColor || "green"}
                                style={{
                                    borderRadius: "50%",
                                    width: 24,
                                    height: 24,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    padding: 0
                                }}
                                icon={<CheckOutlined style={{ color: "white", fontSize: 12 }} />}
                            />
                        ) : null;
                    }

                    // Default tag rendering
                    if (text !== "" && text !== null) {
                        return (
                            <Tag color={column.tagColor || "blue"} style={{ borderRadius: "6px" }}>
                                {text}
                            </Tag>
                        );
                    }

                    return null;
                };
            }


            // Filters
            if (feature && feature.includes("filter")) {
                if (Array.isArray(tableDataInternal)) {
                    const uniqueValues = [
                        ...new Set(tableDataInternal.map((item) => item[dataIndex]))
                    ];

                    columnConfig.filters = uniqueValues
                        .filter(
                            (value) =>
                                value !== null &&
                                value !== undefined &&
                                (typeof value === "string" || typeof value === "number") &&
                                String(value).length > 0
                        )
                        .map((value) => ({ text: String(value), value: String(value) }));

                    columnConfig = {
                        ...columnConfig,
                        filterSearch: true,
                        onFilter: (value, record) => {
                            const dataIndexValue = record[dataIndex];
                            const valueString = String(value).toLowerCase();

                            if (typeof dataIndexValue === "string") {
                                return dataIndexValue.toLowerCase().includes(valueString);
                            } else if (typeof dataIndexValue === "number") {
                                return String(dataIndexValue).includes(valueString);
                            } else {
                                return false;
                            }
                        },
                    };
                }
            }

            // Render actions (supports string or object-based actions)
            if (render && Array.isArray(render)) {
                columnConfig.render = (text, record) => {
                    const actions = [];

                    render.forEach((action, index) => {
                        if (typeof action === 'string') {
                            if (action === "edit" && showEdit) {
                                actions.push(
                                    <Tooltip title="Edit" key={`edit-${index}`}>
                                        <Button
                                            id="useredit"
                                            className="gridIconEdit"
                                            icon={<EditOutlined />}
                                            onClick={() => handleEdit(record)}
                                        />
                                    </Tooltip>
                                );
                            } else if (action === "delete" && showDelete) {
                                actions.push(
                                    <Tooltip title="Delete" key={`delete-${index}`}>
                                        <Popconfirm
                                            title="Are you sure you want to delete?"
                                            onConfirm={() => handleDelete(record)}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button
                                                id="delete"
                                                danger
                                                className="gridIconDelete"
                                                icon={<DeleteOutlined />}
                                            />
                                        </Popconfirm>
                                    </Tooltip>
                                );
                            } else if (action === "view" && showView) {
                                actions.push(
                                    <Tooltip title="View" key={`view-${index}`}>
                                        <Button
                                            // type="primary"
                                            id="view"
                                            className="gridIconView"
                                            icon={<EyeOutlined />}
                                            onClick={() => handleView(record)}
                                        />
                                    </Tooltip>
                                );
                            } else if (action === "viewjson") {
                                actions.push(
                                    <Button
                                        key={`viewjson-${index}`}
                                        size="small"
                                        icon={<EyeOutlined />}
                                        onClick={() => handleViewJson(record)}
                                    >
                                        View JSON
                                    </Button>
                                );
                            }
                        } else if (
                            typeof action === 'object' &&
                            action.type === "view" &&
                            typeof action.onClick === "function"
                        ) {
                            actions.push(
                                <Tooltip title="View" key={`custom-view-${index}`}>
                                    <Button
                                        // type="primary"
                                        icon={<EyeOutlined />}
                                        onClick={() => action.onClick(record)}
                                    />
                                </Tooltip>
                            );
                        }
                    });

                    return <Space>{actions}</Space>;
                };
            }

            return columnConfig;
        });
    };




    const columns = generateColumns(columnData?.columns || []);

    const prepareCSVData = () => {
        const visibleColumns = columns.filter(
            (column) => !column.hidden && column.key !== "action"
        );

        const headerRow = visibleColumns.map((column) => column.title);

        let dataToExport = sortedData && sortedData.length > 0
            ? sortedData
            : tableDataInternal && Array.isArray(tableDataInternal)
                ? tableDataInternal
                : [];

        // Apply search filter
        if (searchText) {
            dataToExport = dataToExport.filter((record) =>
                Object.values(record).some(
                    (value) =>
                        value &&
                        typeof value === "string" &&
                        value.toLowerCase().includes(searchText.toLowerCase())
                )
            );
        }

        const dataRows = dataToExport.map((record) =>
            visibleColumns.map((column) => record[column.dataIndex])
        );

        return [headerRow, ...dataRows];
    };

    const totalRecords = Array.isArray(tableDataInternal)
        ? tableDataInternal.length
        : 0;

    const paginatedData =
        Array.isArray(filteredData) && filteredData.length > 0
            ? filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
            : Array.isArray(tableDataInternal)
                ? tableDataInternal.slice((currentPage - 1) * pageSize, currentPage * pageSize)
                : [];

    const dataIndex = Array.isArray(paginatedData)
        ? paginatedData.filter((record) =>
            Object.values(record).some(
                (value) =>
                    value &&
                    typeof value === "string" &&
                    value.toLowerCase().includes(searchText.toLowerCase())
            )
        )
        : [];

    return (
        <>
            <div className="grid-container">
                <div className="table-actions">
                    <div className="tablesearch">
                        {searchButton && (
                            <Input.Search
                                placeholder="Search"
                                id="search"
                                allowClear
                                value={searchText}
                                style={{ width: 200 }}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                        )}
                    </div>
                    {filterButton && (
                        <Tooltip title="Filter">
                            <Button
                                id="filterbutton"
                                type="primary"
                                icon={<FilterOutlined className="custom-btn-anticon-size" />}
                                className="create-button custom-button-color "
                                onClick={handleCreate}
                            ></Button>
                        </Tooltip>
                    )}

                    {createButton && (
                        // <Tooltip title="Create">
                        <Button
                            id="createbutton"
                            type="primary"
                            icon={<PlusOutlined style={{ color: "white" }} className="custom-btn-anticon-size" />}
                            className="custom-button-color"
                            onClick={handleCreate}
                        >
                            {createButtonName}
                        </Button>
                        // </Tooltip>
                    )}
                    {downloadCSV && (
                        <div className="CSV-actions">
                            <Tooltip title="Download Excel">
                                <CSVLink
                                    data={prepareCSVData()}
                                    filename={`${csvFileName || 'export'}.csv`}
                                    className="custom-button-color"
                                >
                                    <Button
                                        // icon={<RiFileExcel2Line style={{ color: "white" }} className="custom-btn-anticon-size" />}
                                        className="custom-button-color"
                                        style={{ backgroundColor: "#f9a11b", color: "#ffffff", border: "none", borderRadius: "6px !important" }}
                                    />
                                </CSVLink>
                            </Tooltip>
                        </div>
                    )}
                </div>

                <div className="grid-table-container">
                    <Row>
                        <Col span={24}>
                            <Table
                                onChange={handleTableChange}
                                columns={columns}
                                pagination={false} // disable default pagination
                                dataSource={dataIndex}
                                rowKey={rowKey}
                                bordered
                                scroll={{
                                    x: '100%',
                                    y: totalRecords > 0 ? 'calc(100vh - 400px)' : undefined,
                                }}
                                size="small"
                                responsive={['xs', 'sm', 'lg', 'md', 'xl', 'xll']}
                                rowClassName={(record, index) => (index % 2 === 0 ? 'even-row' : 'odd-row')}
                                className={className}
                                rowSelection={
                                    enableRowSelection
                                        ? {
                                            type: 'checkbox',
                                            onChange: (selectedKeys, selectedRows) => {
                                                onRowSelectionChange(selectedKeys, selectedRows);
                                            },
                                        }
                                        : null
                                }
                                expandable={
                                    expandableRowRender
                                        ? {
                                            expandedRowRender: expandableRowRender,
                                            rowExpandable: () => true,
                                        }
                                        : undefined
                                }
                            />
                        </Col>

                        {showPagination && (
                            <Col span={24} style={{ paddingTop: '10px' }}>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        {dataIndex.length > 0 && (
                                            <span style={{ fontWeight: 'bold', color: '#333' }}>
                                                Total Records: {totalRecords}
                                            </span>
                                        )}
                                    </Col>
                                    <Col>
                                        <Pagination
                                            current={currentPage}
                                            pageSize={pageSize}
                                            total={totalRecords}
                                            showSizeChanger
                                            pageSizeOptions={['10', '20', '50', '100']}
                                            showQuickJumper
                                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                                            onChange={(page, size) => {
                                                setCurrentPage(page);
                                                setPageSize(size);
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>

                </div>
            </div>
        </>
    );
};

export default Grid;

